<script setup>
import {computed, onBeforeUpdate, onMounted, ref, watch} from "vue";
import _, {debounce, isEmpty} from "lodash";
import {usePiniaStore} from "../store/pinia_store";
import {storeToRefs} from "pinia";

const props = defineProps({
    brandTitle: {
        type: String,
        required: true,
    },
    productTitle: {
        type: String,
        required: true,
    },
    dentaidApiInstance: {
        type: Function,
        required: true,
    },
    cartEventBus: {
        type: Object,
        required: true,
    },
    eventBus: {
        type: Object,
        required: true,
    },
    initialQuantity: {
        type: Number,
        required: false,
    },
    crmId: {
        type: String,
        required: true,
    },
    orderUnit: {
        type: Number,
        required: true,
    },
    incrementalOrderUnit: {
        type: Number,
        required: true,
    }
})

const piniaStore = usePiniaStore();
const {cart} = storeToRefs(piniaStore);

const quantity = ref(props.orderUnit);
const increment = ref(false);
const loading = ref(false);
const netTotalPrice = ref(null);
const addToCartMessage = window.Drupal.t('Product succesfully added to cart!');
const addToCartMessageError = window.Drupal.t('Something went wrong when adding a product to the cart.');

const showRemove = computed(() => {
    return _.isEqual(quantity.value, props.orderUnit);
})

const handleClick = () => {
    if (!increment.value) {
        addToCart();

        increment.value = true;

        return;
    }

    quantity.value = quantity.value + props.incrementalOrderUnit;

    addToCart();
}

const addToCart = debounce(() => {
    loading.value = true;

    props.dentaidApiInstance.post(`/cart?token=${piniaStore.parsedToken}`, {
        items: {
            "0": {
                sku: props.crmId,
                quantity: quantity.value,
            }
        }
    })
        .then(response => {
            piniaStore.updateUserData(response.data.data);

            pushToDataLayer();
        })
        .catch(error => {
            Vue.$toast.error(addToCartMessageError)
        })
        .finally(() => {
            loading.value = false;
        });
}, 250);

const handleRemove = debounce(() => {
    loading.value = true;

    props.dentaidApiInstance.post(`/cart/delete?token=${piniaStore.parsedToken}`, {
        items: {
            "0": {
                sku: props.crmId,
            }
        }
    })
        .then(response => {
            piniaStore.updateUserData(response.data.data);

            pushRemoveFromCartToDataLayer();
        })
        .catch(error => {
            Vue.$toast.error('Er is iets fout gegaan!');
        })
        .finally(() => {
            increment.value = false;
            loading.value = false;
        });
}, 250)

const handleDecrement = () => {
    quantity.value = quantity.value - props.incrementalOrderUnit;

    addToCart();
}

const formatPriceForDataLayer = (priceInCents) => {
    return (priceInCents / 100).toFixed(2);
}

const calculateUnitDiscount = (product) => {
    const totalUnitPrice = product.total_unit_price || 0;
    const totalNetPrice = product.total_net_price || 0;
    const quantity = product.quantity || 1;
    const discountPerUnit = (totalUnitPrice - totalNetPrice) / quantity;
    return formatPriceForDataLayer(discountPerUnit);
}

const calculateTotalProductDiscount = (product) => {
    const totalUnitPrice = product.total_unit_price || 0;
    const totalNetPrice = product.total_net_price || 0;
    const totalDiscount = totalUnitPrice - totalNetPrice;
    return formatPriceForDataLayer(totalDiscount);
}

const getAppliedVoucherCodes = () => {
    const voucherLines = _.filter(piniaStore.cart.lines, (line) => line.type === 'Voucher');
    const voucherCodes = _.map(voucherLines, (voucher) => voucher.code || voucher.name);
    return voucherCodes.join(', ');
}

const pushToDataLayer = () => {
    // Get the product from the cart to access all properties including price and discounts
    const cartProduct = _.find(piniaStore.cart.lines, (line) => line.sku === props.crmId && line.type === 'Product');

    if (!cartProduct) {
        // Fallback to basic data if product not found in cart yet
        window.dataLayer.push({ecommerce: null});
        window.dataLayer.push({
            'event': 'addToCart',
            'ecommerce': {
                'currencyCode': 'EUR',
                'add': {
                    'products': [{
                        'name': props.productTitle?.toLowerCase(),
                        'id': props.crmId,
                        'brand': props.brandTitle,
                        'quantity': quantity.value
                    }]
                }
            }
        });
        return;
    }

    const appliedCoupons = getAppliedVoucherCodes();
    const totalProductPrice = formatPriceForDataLayer(cartProduct.unit_price * cartProduct.quantity);
    const totalProductDiscount = calculateTotalProductDiscount(cartProduct);

    window.dataLayer.push({ecommerce: null});

    window.dataLayer.push({
        'event': 'addToCart',
        'ecommerce': {
            'currencyCode': 'EUR',
            'add': {
                'products': [{
                    'name': props.productTitle?.toLowerCase(),
                    'id': props.crmId,
                    'brand': props.brandTitle,
                    'price': formatPriceForDataLayer(cartProduct.unit_price),
                    'quantity': cartProduct.quantity,
                    'discount': calculateUnitDiscount(cartProduct),
                    'total_product_price': (parseFloat(totalProductPrice) - parseFloat(totalProductDiscount)).toFixed(2),
                    'total_product_discount': totalProductDiscount,
                    ...(appliedCoupons && { 'coupon': appliedCoupons })
                }]
            }
        }
    });
}

const pushRemoveFromCartToDataLayer = () => {
    // Get the product from the cart before removal to access all properties
    const cartProduct = _.find(piniaStore.cart.lines, (line) => line.sku === props.crmId && line.type === 'Product');

    if (!cartProduct) {
        // Fallback to basic data if product not found in cart
        window.dataLayer.push({ecommerce: null});
        window.dataLayer.push({
            'event': 'removeFromCart',
            'ecommerce': {
                'remove': {
                    'products': [{
                        'name': props.productTitle?.toLowerCase(),
                        'id': props.crmId,
                        'brand': props.brandTitle,
                        'quantity': quantity.value
                    }]
                }
            }
        });
        return;
    }

    const appliedCoupons = getAppliedVoucherCodes();
    const totalProductPrice = formatPriceForDataLayer(cartProduct.unit_price * cartProduct.quantity);
    const totalProductDiscount = calculateTotalProductDiscount(cartProduct);

    window.dataLayer.push({ecommerce: null});

    window.dataLayer.push({
        'event': 'removeFromCart',
        'ecommerce': {
            'remove': {
                'products': [{
                    'name': props.productTitle?.toLowerCase(),
                    'id': props.crmId,
                    'brand': props.brandTitle,
                    'price': formatPriceForDataLayer(cartProduct.unit_price),
                    'quantity': cartProduct.quantity,
                    'discount': calculateUnitDiscount(cartProduct),
                    'total_product_price': (parseFloat(totalProductPrice) - parseFloat(totalProductDiscount)).toFixed(2),
                    'total_product_discount': totalProductDiscount,
                    ...(appliedCoupons && { 'coupon': appliedCoupons })
                }]
            }
        }
    });
}

watch(() => piniaStore.isLoading, () => {
    if (!isEmpty(cart.value.lines?.[props.crmId] ?? null) && !piniaStore.isLoading) {
        setTimeout(() => {
            quantity.value = cart.value.lines[props.crmId].quantity;

            increment.value = true;
        }, 100)
    }
}, {deep: true})

onMounted(() => {
    props.eventBus.$on('load-price', (payload) => {
        netTotalPrice.value = payload.net_total_price;
    });

    props.eventBus.$emit('set-quantity', {
        quantity: props.orderUnit,
        crmId: props.crmId
    });

    if (!isEmpty(cart.value.lines?.[props.crmId] ?? null) && !piniaStore.isLoading) {
        setTimeout(() => {
            quantity.value = cart.value.lines[props.crmId].quantity;

            increment.value = true;
        }, 100)
    }
})
</script>

<template>
    <div class="vue-product-teaser-add-to-cart">
        <template v-if="increment">
            <button
                v-if="showRemove"
                @click="handleRemove()"
                class="btn-primary"
                :disabled="loading"
            >
                <dentaid-trash-icon></dentaid-trash-icon>
            </button>
            <button
                v-if="!showRemove"
                @click="handleDecrement()"
                class="btn-primary"
                :disabled="loading"
            >
                -
            </button>

            <input
                v-model="quantity"
                :min="orderUnit"
                :step="incrementalOrderUnit"
                type="number"
                readonly="readonly"
            >
        </template>

        <button
            @click="handleClick()"
            :disabled="loading"
            class="btn-primary">+
        </button>
    </div>
</template>